import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Space,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import { DatePicker, Select, toast } from "src/components";
import {
  AddQuestsToCampaignRequest,
  buildExtraConfig,
  GAME_PROVIDER_OPTIONS,
  getExtraConfigFields,
  getQuestTypeLabel,
  getRewardTypeLabel,
  getTargetValueLabel,
  getTargetValueUnit,
  QuestDetail,
  PRODUCT_TYPE_OPTIONS,
  QuestItem,
  QuestType,
  RewardType,
  useAddQuestsToCampaign,
  useListAffiliateCampaigns,
} from "src/hooks/affiliate";
import { toBangkokNaive } from "src/utils";
import { findQuestTemplate, QUEST_TEMPLATES, QuestTemplate } from "./QuestTemplates";

const { Text } = Typography;

interface ConditionFormItem {
  isUniqueUser?: boolean;
  // ค่าของ extra_config แยกเป็น field ตาม quest type (เก็บเป็น object ไม่ใช่ JSON string)
  extra?: Record<string, unknown>;
}

interface BonusFormItem {
  description?: string;
  durationInHours?: number;
  initialCoinAmount?: number;
  bonusRatio?: number;
  turnoverRatio?: number;
  maximumBonus?: number;
  maximumPerDay?: number;
  convertLimitAmount?: number;
  convertLimitRatio?: number;
}

interface QuestFormItem {
  questName: string;
  questDescription?: string;
  questType: QuestType;
  targetValue: number;
  dateRange: [Dayjs, Dayjs];
  rewardType: RewardType;
  rewardValue?: number;
  order?: number;
  isActive?: boolean;
  tier?: number;
  isAllTier?: boolean;
  conditions?: ConditionFormItem[];
  bonuses?: BonusFormItem[];
}

interface QuestFormValues {
  campaignCode: string;
  quests: QuestFormItem[];
}

const questTypeOptions = Object.values(QuestType).map((v) => ({
  value: v,
  label: getQuestTypeLabel(v),
}));
const rewardTypeOptions = Object.values(RewardType).map((v) => ({ value: v, label: getRewardTypeLabel(v) }));

/** ครบเงื่อนไขขั้นต่ำที่จะสรุปได้ไหม (required fields ของ quest) */
const isQuestComplete = (q?: QuestFormItem): boolean => {
  if (!q) return false;
  const rewardOk =
    q.rewardType === RewardType.BONUS ||
    (q.rewardValue !== undefined && q.rewardValue !== null);
  return Boolean(
    q.questName &&
      q.questType &&
      q.targetValue !== undefined &&
      q.targetValue !== null &&
      q.dateRange?.[0] &&
      q.dateRange?.[1] &&
      q.rewardType &&
      rewardOk,
  );
};

/** ขอบเขตเกม (ค่าย/เกม/product) เป็นข้อความสั้น ๆ จาก extra_config */
const scopeText = (ec: Record<string, unknown>): string => {
  if (ec.game_id) return `เกม ${ec.game_id}`;
  if (ec.game_provider) return `ค่าย ${ec.game_provider}`;
  if (ec.product_type) return `${ec.product_type}`;
  return "";
};

/** ประโยคบรรยาย "เงื่อนไขที่ต้องทำ" ตาม quest type + ค่าในฟอร์ม */
const buildConditionPhrase = (
  quest: QuestFormItem,
  ec: Record<string, unknown>,
): string => {
  const n = Number(quest.targetValue).toLocaleString();
  const scope = scopeText(ec);
  switch (quest.questType) {
    case QuestType.REFERRAL_COUNT:
      return `คนที่ชวนมาฝากเงินครั้งแรกได้ ${n} คน`;
    case QuestType.REFERRAL_STACK:
      return `คนที่ชวนมาฝากซ้ำตั้งแต่ ${ec.min_per_user ?? 2} ครั้งขึ้นไป จำนวน ${n} คน`;
    case QuestType.REFERRAL_TURNOVER:
      return `ยอดเดิมพันรวมของคนที่ชวนมาถึง ${n} บาท`;
    case QuestType.REFERRAL_TURNOVER_CONTINUE:
      return `คนที่ชวนมาเล่นต่อเนื่อง ${ec.min_streak ?? 2} วันขึ้นไป จำนวน ${n} คน`;
    case QuestType.REFERRAL_INVITE_PLAYERS:
      return `คนที่ชวนมาเล่น${scope ? `${scope} ` : ""}จำนวน ${n} คน`;
    case QuestType.REFERRAL_INVITE_PROVIDER_ACTIVE_DURATION:
      return `คนที่ชวนมาเล่น${scope ? `${scope} ` : ""}ครบ ${ec.min_active_days ?? 1} วัน จำนวน ${n} คน`;
    case QuestType.AFFILIATE_INVITE_CONSECUTIVE_PLAYERS:
      return `downline เล่นต่อเนื่อง ${n} วัน${
        Number(ec.min_downlines_per_day ?? 1) > 1 ? ` (วันละ ≥ ${ec.min_downlines_per_day} คน)` : ""
      }`;
    case QuestType.REFERRAL_TURNOVER_GAME_PROVIDERS:
      return `คนที่ชวนมามียอดเดิมพันสะสม${scope ? `ใน${scope} ` : " "}ถึง ${Number(
        ec.min_turnover ?? 0,
      ).toLocaleString()} บาท จำนวน ${n} คน`;
    case QuestType.REFERRAL_INVITE_DISTINCT_GAME_PROVIDERS:
      return `คนที่ชวนมาเล่นเกมต่างกัน ${ec.min_distinct_games ?? 1} เกม จำนวน ${n} คน`;
    case QuestType.REFERRAL_INVITE_COMEBACK_PLAYERS:
      return `ดึงคนที่ชวนมาแล้วหายไป ${ec.inactive_days ?? 30} วันขึ้นไปให้กลับมาเล่น (สูงสุด ${n} คน)`;
    default:
      return `${n} ${getTargetValueUnit(quest.questType)}`;
  }
};

/** ประโยคบรรยายรางวัล (รวมรายละเอียดโบนัสถ้าเป็น BONUS) */
const buildRewardPhrase = (quest: QuestFormItem): string => {
  const value = quest.rewardValue != null ? Number(quest.rewardValue).toLocaleString() : "-";
  if (quest.rewardType === RewardType.COMMISSION_PERCENTAGE) {
    return `เพิ่มค่าคอมมิชชัน +${value}%`;
  }
  if (quest.rewardType === RewardType.BONUS) {
    const b = quest.bonuses?.[0];
    const amount = Number(b?.initialCoinAmount ?? 0).toLocaleString();
    const turn = b?.turnoverRatio ? `เทิร์น ${b.turnoverRatio} เท่า` : "ไม่มีเทิร์น";
    const expire = b?.durationInHours
      ? `หมดอายุ ${Math.round(b.durationInHours / 24)} วัน`
      : "ไม่มีวันหมดอายุ";
    return `${amount} บาท (โบนัส / ${turn} / ${expire})`;
  }
  // FIXED_MONEY
  return `${value} บาท เงินสด`;
};

const QuestSummary: React.FC<{ quest: QuestFormItem }> = ({ quest }) => {
  const [start, end] = quest.dateRange || [];
  const ec = buildExtraConfig(quest.questType, quest.conditions?.[0]?.extra) || {};
  const sentence = `${buildConditionPhrase(quest, ec)} → ${buildRewardPhrase(quest)}`;
  const days = start && end ? end.diff(start, "day") : null;
  const period =
    start && end
      ? `${start.format("DD/MM/YYYY")} – ${end.format("DD/MM/YYYY")}${
          days != null ? ` (${days} วัน)` : ""
        }`
      : "";
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginTop: 12 }}
      message="สรุปข้อมูลเควส"
      description={
        <div style={{ fontSize: 13 }}>
          <div style={{ fontWeight: 500 }}>{sentence}</div>
          {period && (
            <div style={{ marginTop: 2 }}>
              <Text type="secondary" italic>
                {period}
              </Text>
            </div>
          )}
        </div>
      }
    />
  );
};

export type AddQuestModalProps = {
  onSuccess?: () => void;
  prefillQuest?: QuestDetail;
};

const buildPrefillInitialValues = (q: QuestDetail): QuestFormValues => ({
  campaignCode: q.campaignCode ?? "",
  quests: [
    {
      questName: q.questName,
      questDescription: q.questDescription ?? undefined,
      questType: q.questType,
      targetValue: q.targetValue,
      dateRange: [dayjs(q.startDate), dayjs(q.endDate)] as [Dayjs, Dayjs],
      rewardType: q.rewardType,
      rewardValue: q.rewardValue ?? undefined,
      order: q.order ?? undefined,
      isActive: q.isActive,
      tier: q.tier ?? undefined,
      isAllTier: q.isAllTier ?? false,
      conditions: (q.conditions || []).map((c) => ({
        isUniqueUser: c.isUniqueUser ?? false,
        extra: c.extraConfig || {},
      })),
      bonuses: (q.bonuses || []).map((b) => ({
        description: b.description ?? undefined,
        durationInHours: b.durationInHours ?? undefined,
        initialCoinAmount: b.initialCoinAmount ?? undefined,
        bonusRatio: b.bonusRatio ?? undefined,
        turnoverRatio: b.turnoverRatio ?? undefined,
        maximumBonus: b.maximumBonus ?? undefined,
        maximumPerDay: b.maximumPerDay ?? undefined,
        convertLimitAmount: b.convertLimitAmount ?? undefined,
        convertLimitRatio: b.convertLimitRatio ?? undefined,
      })),
    },
  ],
});

const DEFAULT_INITIAL_VALUES = {
  quests: [
    {
      isActive: true,
      conditions: [],
      bonuses: [],
    },
  ],
};

type AddMode = "manual" | "template";

export const AddQuestModal = NiceModal.create(({ ...props }: AddQuestModalProps) => {
  const { visible, hide, remove } = useModal();
  const [form] = Form.useForm<QuestFormValues>();
  const isPrefill = !!props.prefillQuest;
  const initialValues = React.useMemo(
    () => (props.prefillQuest ? buildPrefillInitialValues(props.prefillQuest) : DEFAULT_INITIAL_VALUES),
    [props.prefillQuest],
  );
  // When user opens modal via Copy → start in manual mode (and skip the toggle).
  // Otherwise default to template mode for easier authoring.
  const [mode, setMode] = React.useState<AddMode>(isPrefill ? "manual" : "template");
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | undefined>(undefined);

  const { mutate: addQuests, isLoading } = useAddQuestsToCampaign({
    onSuccess: () => {
      form.resetFields();
      setSelectedTemplateId(undefined);
      props.onSuccess?.();
      hide();
    },
  });

  const { data: campaignList, isLoading: isCampaignsLoading } = useListAffiliateCampaigns();
  const campaignOptions = React.useMemo(() => {
    const now = dayjs();
    return (campaignList?.campaigns || [])
      // แสดงเฉพาะ campaign ที่ยังไม่หมดอายุ (end_date >= ตอนนี้)
      .filter((c) => !c.endDate || dayjs(c.endDate).isAfter(now))
      .map((c) => ({
        value: c.campaignCode,
        label: `${c.campaignCode} — ${c.campaignName}`,
      }));
  }, [campaignList]);

  const handleClose = () => {
    hide();
  };

  const applyTemplate = (template: QuestTemplate) => {
    const now = dayjs();
    const end = now.add(template.defaultDurationDays, "day");
    form.setFieldsValue({
      quests: [
        {
          questName: template.questName,
          questType: template.questType,
          targetValue: template.targetValue,
          dateRange: [now, end] as [Dayjs, Dayjs],
          rewardType: template.rewardType,
          rewardValue: template.rewardValue,
          order: undefined,
          isActive: true,
          tier: undefined,
          isAllTier: false,
          conditions: [
            {
              isUniqueUser: template.conditionIsUniqueUser ?? false,
              extra: template.extraConfig || {},
            },
          ],
          bonuses: (template.bonuses || []).map((b) => ({ ...b })),
        },
      ],
    });
  };

  const handleSelectTemplate = (id: string) => {
    setSelectedTemplateId(id);
    const tpl = findQuestTemplate(id);
    if (tpl) applyTemplate(tpl);
  };

  const handleSwitchMode = (nextMode: AddMode) => {
    setMode(nextMode);
    if (nextMode === "manual") {
      // Reset to empty form when switching to Manual (user expectation: start fresh)
      setSelectedTemplateId(undefined);
      form.resetFields();
    }
  };

  const handleFinish = (values: QuestFormValues) => {
    if (!values.quests || values.quests.length === 0) {
      toast.error("ต้องมีอย่างน้อย 1 quest");
      return;
    }

    let invalidDate = false;
    let missingBonus = false;
    const quests: QuestItem[] = values.quests.map((q) => {
      const [qStart, qEnd] = q.dateRange || [];
      if (!qStart || !qEnd) {
        invalidDate = true;
      }
      // reward_type = BONUS → ต้องมี affiliate_campaign_bonus อย่างน้อย 1 รายการ (มี Initial Coin)
      if (
        q.rewardType === RewardType.BONUS &&
        !(q.bonuses || []).some((b) => Number(b.initialCoinAmount ?? 0) > 0)
      ) {
        missingBonus = true;
      }
      return {
        questName: q.questName,
        questDescription: q.questDescription,
        questType: q.questType,
        targetValue: Number(q.targetValue),
        startDate: qStart ? toBangkokNaive(qStart) : "",
        endDate: qEnd ? toBangkokNaive(qEnd) : "",
        rewardType: q.rewardType,
        rewardValue: q.rewardValue != null ? Number(q.rewardValue).toString() : undefined,
        order: q.order,
        isActive: q.isActive,
        tier: q.isAllTier ? null : q.tier ?? null,
        isAllTier: q.isAllTier,
        conditions: (q.conditions || []).map((c) => {
          const extraConfig = buildExtraConfig(q.questType, c.extra);
          return {
            // metricType/operator/targetValue dropped — backend keys off quest.questType
            timeWindowDay: null,
            isUniqueUser: c.isUniqueUser ?? false,
            extraConfig,
          };
        }),
        bonuses: (q.bonuses || []).map((b) => ({
          description: b.description,
          durationInHours: b.durationInHours ?? 0,
          initialCoinAmount: b.initialCoinAmount ?? 0,
          bonusRatio: b.bonusRatio ?? 0,
          turnoverRatio: b.turnoverRatio ?? 0,
          maximumBonus: b.maximumBonus ?? 0,
          maximumPerDay: b.maximumPerDay ?? 0,
          convertLimitAmount: b.convertLimitAmount ?? 0,
          convertLimitRatio: b.convertLimitRatio ?? 1,
        })),
      };
    });

    if (invalidDate) {
      toast.error("กรุณาเลือกวันที่ของ quest ให้ครบ");
      return;
    }
    if (missingBonus) {
      toast.error("reward type โบนัส ต้องเพิ่ม Bonus อย่างน้อย 1 รายการ (ระบุ Initial Coin)");
      return;
    }

    const payload: AddQuestsToCampaignRequest = {
      campaignCode: values.campaignCode,
      quests,
    };
    addQuests(payload);
  };

  return (
    <Modal
      centered
      width={1000}
      visible={visible}
      afterClose={remove}
      closable={false}
      title={
        isPrefill
          ? "เพิ่ม Quest (Copy จาก quest เดิม)"
          : mode === "template"
          ? "เพิ่ม Quest (Template)"
          : "เพิ่ม Quest (Manual)"
      }
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
          <Button key="cancel" size="large" onClick={handleClose} style={{ width: 100 }}>
            ยกเลิก
          </Button>
          <Button
            key="submit"
            type="primary"
            size="large"
            loading={isLoading}
            onClick={() => form.submit()}
            style={{ width: 200 }}
          >
            เพิ่ม Quest
          </Button>
        </div>
      }
    >
      {!isPrefill && (
        <div style={{ marginBottom: 12 }}>
          <Space size="middle" align="center">
            <Text strong>โหมดเพิ่ม Quest:</Text>
            <Radio.Group
              value={mode}
              onChange={(e) => handleSwitchMode(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="template">Template</Radio.Button>
              <Radio.Button value="manual">Manual</Radio.Button>
            </Radio.Group>
            {mode === "template" && (
              <Select
                width="460px"
                showSearch
                optionFilterProp="label"
                placeholder="เลือก template..."
                value={selectedTemplateId}
                onChange={handleSelectTemplate}
                options={QUEST_TEMPLATES.map((t) => ({ value: t.id, label: t.name }))}
              />
            )}
          </Space>
          {mode === "template" && selectedTemplateId && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 8 }}
              message={findQuestTemplate(selectedTemplateId)?.name}
              description={findQuestTemplate(selectedTemplateId)?.description}
            />
          )}
          {mode === "template" && !selectedTemplateId && (
            <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
              เลือก template ด้านบนเพื่อ pre-fill ฟอร์ม แล้วปรับ campaign code / วันที่ / reward ตามต้องการ
            </Text>
          )}
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleFinish}
        preserve={false}
        initialValues={initialValues}
        style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 8 }}
      >
        <Card title="Campaign" size="small">
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                label="Campaign Code"
                name="campaignCode"
                rules={[{ required: true, message: "เลือก campaign" }]}
              >
                <Select
                  width="100%"
                  showSearch
                  loading={isCampaignsLoading}
                  placeholder="เลือก campaign"
                  options={campaignOptions}
                  optionFilterProp="label"
                  notFoundContent={isCampaignsLoading ? "กำลังโหลด..." : "ไม่พบ campaign"}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Divider />

        <Card title="Quests" size="small">
          <Form.List name="quests">
            {(questFields, { add: addQuest, remove: removeQuest }) => (
              <>
                {questFields.map((questField) => (
                  <Card
                    key={questField.key}
                    type="inner"
                    size="small"
                    style={{ marginBottom: 16 }}
                    title={`Quest #${questField.name + 1}`}
                    extra={<MinusCircleOutlined onClick={() => removeQuest(questField.name)} />}
                  >
                    <Row gutter={[16, 0]}>
                      <Col span={8}>
                        <Form.Item
                          label="ชื่อ Quest"
                          name={[questField.name, "questName"]}
                          rules={[{ required: true, message: "ระบุชื่อ quest" }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="Quest Type"
                          name={[questField.name, "questType"]}
                          rules={[{ required: true, message: "เลือก quest type" }]}
                        >
                          <Select options={questTypeOptions} width="100%" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, cur) =>
                            prev.quests?.[questField.name]?.questType !==
                            cur.quests?.[questField.name]?.questType
                          }
                        >
                          {({ getFieldValue }) => (
                            <Form.Item
                              label={getTargetValueLabel(
                                getFieldValue(["quests", questField.name, "questType"]),
                              )}
                              name={[questField.name, "targetValue"]}
                              rules={[{ required: true, message: "ระบุ target value" }]}
                            >
                              <InputNumber min={0} style={{ width: "100%" }} />
                            </Form.Item>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="ช่วงเวลา Quest"
                          name={[questField.name, "dateRange"]}
                          rules={[{ required: true, message: "เลือกช่วงเวลา" }]}
                        >
                          <DatePicker.RangePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY HH:mm"
                            showTime={{ format: "HH:mm" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          label="Reward Type"
                          name={[questField.name, "rewardType"]}
                          rules={[{ required: true, message: "เลือก reward type" }]}
                        >
                          <Select options={rewardTypeOptions} width="100%" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Reward Value" name={[questField.name, "rewardValue"]}>
                          <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item label="คำอธิบาย" name={[questField.name, "questDescription"]}>
                          <Input.TextArea rows={2} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item label="Order" name={[questField.name, "order"]}>
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, cur) =>
                            prev.quests?.[questField.name]?.isAllTier !==
                            cur.quests?.[questField.name]?.isAllTier
                          }
                        >
                          {({ getFieldValue }) => {
                            const allTier = getFieldValue(["quests", questField.name, "isAllTier"]);
                            return (
                              <Form.Item
                                label="Tier"
                                name={[questField.name, "tier"]}
                                rules={[
                                  {
                                    required: !allTier,
                                    message: "ระบุ tier หรือเลือก All Tier",
                                  },
                                ]}
                              >
                                <InputNumber min={1} style={{ width: "100%" }} disabled={allTier} />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[questField.name, "isAllTier"]}
                          valuePropName="checked"
                          label=" "
                        >
                          <Checkbox
                            onChange={(e) => {
                              if (e.target.checked) {
                                form.setFields([
                                  {
                                    name: ["quests", questField.name, "tier"],
                                    value: undefined,
                                    errors: [],
                                  },
                                ]);
                              }
                            }}
                          >
                            All Tier
                          </Checkbox>
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[questField.name, "isActive"]}
                          valuePropName="checked"
                          label=" "
                          initialValue
                        >
                          <Checkbox>Active</Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Divider orientation="left" plain>
                      Conditions
                    </Divider>
                    <Form.List name={[questField.name, "conditions"]}>
                      {(condFields, { add: addCond, remove: removeCond }) => (
                        <>
                          {condFields.map((condField) => (
                            <Row gutter={[16, 0]} key={condField.key} align="top">
                              <Col span={4}>
                                <Form.Item
                                  name={[condField.name, "isUniqueUser"]}
                                  valuePropName="checked"
                                  label=" "
                                >
                                  <Checkbox>Unique</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col span={19}>
                                <Form.Item
                                  noStyle
                                  shouldUpdate={(prev, cur) =>
                                    prev.quests?.[questField.name]?.questType !==
                                    cur.quests?.[questField.name]?.questType
                                  }
                                >
                                  {({ getFieldValue }) => {
                                    const qType = getFieldValue([
                                      "quests",
                                      questField.name,
                                      "questType",
                                    ]);
                                    const fields = getExtraConfigFields(qType);
                                    if (!fields.length) {
                                      return (
                                        <Form.Item label="Extra Config">
                                          <Text type="secondary">
                                            {qType
                                              ? "quest type นี้ไม่มี extra config"
                                              : "เลือก Quest Type ก่อน"}
                                          </Text>
                                        </Form.Item>
                                      );
                                    }
                                    return (
                                      <Row gutter={[16, 0]}>
                                        {fields.map((f) => (
                                          <Col span={12} key={f.key}>
                                            <Form.Item
                                              label={f.label}
                                              name={[condField.name, "extra", f.key]}
                                              rules={
                                                f.required
                                                  ? [{ required: true, message: `ระบุ ${f.label}` }]
                                                  : undefined
                                              }
                                            >
                                              {f.kind === "number" ? (
                                                <InputNumber
                                                  min={0}
                                                  style={{ width: "100%" }}
                                                  placeholder={f.placeholder}
                                                />
                                              ) : f.kind === "product_type" ? (
                                                <Select
                                                  allowClear
                                                  width="100%"
                                                  placeholder="ทุก product"
                                                  options={PRODUCT_TYPE_OPTIONS}
                                                />
                                              ) : f.kind === "game_provider" ? (
                                                <Select
                                                  allowClear
                                                  showSearch
                                                  width="100%"
                                                  placeholder="เลือกค่ายเกม"
                                                  optionFilterProp="label"
                                                  options={GAME_PROVIDER_OPTIONS}
                                                />
                                              ) : (
                                                <Input placeholder={f.placeholder} />
                                              )}
                                            </Form.Item>
                                          </Col>
                                        ))}
                                      </Row>
                                    );
                                  }}
                                </Form.Item>
                              </Col>
                              <Col span={1} style={{ display: "flex", alignItems: "center" }}>
                                <MinusCircleOutlined onClick={() => removeCond(condField.name)} />
                              </Col>
                            </Row>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => addCond()}
                            icon={<PlusOutlined />}
                            block
                          >
                            เพิ่ม Condition
                          </Button>
                        </>
                      )}
                    </Form.List>

                    <Divider orientation="left" plain>
                      Bonuses
                    </Divider>
                    <Form.List name={[questField.name, "bonuses"]}>
                      {(bonusFields, { add: addBonus, remove: removeBonus }) => (
                        <>
                          {bonusFields.map((bonusField) => (
                            <Card
                              key={bonusField.key}
                              size="small"
                              style={{ marginBottom: 12, background: "#fafafa" }}
                              type="inner"
                              title={`Bonus #${bonusField.name + 1}`}
                              extra={
                                <MinusCircleOutlined
                                  onClick={() => removeBonus(bonusField.name)}
                                />
                              }
                            >
                              <Row gutter={[16, 0]}>
                                <Col span={12}>
                                  <Form.Item
                                    label="คำอธิบาย"
                                    name={[bonusField.name, "description"]}
                                  >
                                    <Input />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Duration (hours)"
                                    name={[bonusField.name, "durationInHours"]}
                                  >
                                    <InputNumber min={0} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Initial Coin"
                                    name={[bonusField.name, "initialCoinAmount"]}
                                  >
                                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Bonus Ratio"
                                    name={[bonusField.name, "bonusRatio"]}
                                  >
                                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Turnover Ratio"
                                    name={[bonusField.name, "turnoverRatio"]}
                                  >
                                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Maximum Bonus"
                                    name={[bonusField.name, "maximumBonus"]}
                                  >
                                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Max per Day"
                                    name={[bonusField.name, "maximumPerDay"]}
                                  >
                                    <InputNumber min={0} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Convert Limit Amount"
                                    name={[bonusField.name, "convertLimitAmount"]}
                                  >
                                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                                <Col span={6}>
                                  <Form.Item
                                    label="Convert Limit Ratio"
                                    name={[bonusField.name, "convertLimitRatio"]}
                                  >
                                    <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Card>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => addBonus()}
                            icon={<PlusOutlined />}
                            block
                          >
                            เพิ่ม Bonus
                          </Button>
                        </>
                      )}
                    </Form.List>

                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue }) => {
                        const q = getFieldValue(["quests", questField.name]) as QuestFormItem;
                        return isQuestComplete(q) ? <QuestSummary quest={q} /> : null;
                      }}
                    </Form.Item>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    addQuest({
                      isActive: true,
                      conditions: [],
                      bonuses: [],
                    })
                  }
                  icon={<PlusOutlined />}
                  block
                >
                  เพิ่ม Quest
                </Button>
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </Modal>
  );
});

export default AddQuestModal;
